"use client"

import React, { useMemo } from "react"
import Image from "next/image"
import { BadgeCheck, Phone, Mail, MapPin } from "lucide-react"

import { Button } from "@/components/atoms/Button/button"
import { HeadingText } from "@/components/atoms/HeadingText/heading-text"
import Text from "@/components/atoms/Text/text"
import { platformIcons } from "@/components/organisms/BlogDetail/social"
import SocialLinks from "@/components/molecules/SocialLinks"
import { ContentReader } from "@/components/molecules/ContentReader"
import { useAgent } from "@/hooks/useAgent"
import { ReactQueryProvider } from "@/app/contexts/QueryProvider"

const generateEmailDraft = (email: string, property: string, address: string): string => {
  const subject = `Inquiry About ${property}`
  const body = `Hello,\n\nI am interested in learning more about the property located at ${address}. Could you please provide additional details?\n\nThank you,\n[Your Name]`
  return `mailto:${encodeURIComponent(email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
}

const AgentDetail = ({
  detail,
  translations,
}: {
  detail: {
    id: string
    agentId: string
    title: string
    location: { address: string }
  }
  translations: Record<string, string>
}) => {
  const { data: agent, isLoading, error } = useAgent(detail.agentId)

  const social = useMemo(() => agent?.social || [], [agent])

  const mailtoLink = useMemo(() => {
    if (!agent?.email) return "#"
    return generateEmailDraft(agent.email, detail.title, detail.location.address)
  }, [agent?.email, detail.title, detail.location.address])

  const socialLinks = useMemo(() => {
    return social
      .filter((s: { platform: string }) => s.platform && !!platformIcons[s.platform])
      .map((s: { url: string; platform: string }) => ({
        href: s.url,
        label: s.platform,
        icon: platformIcons[s.platform],
      }))
  }, [social])

  if (isLoading) {
    return (
      <div className="flex h-64 w-full animate-pulse flex-col items-center justify-center rounded-xl border border-zinc-200 bg-white p-6 dark:border-zinc-700 dark:bg-zinc-900">
        <div className="h-24 w-24 rounded-full bg-zinc-200 dark:bg-zinc-700" />
        <div className="mt-4 h-6 w-48 rounded bg-zinc-200 dark:bg-zinc-700" />
        <div className="mt-2 h-4 w-32 rounded bg-zinc-200 dark:bg-zinc-700" />
      </div>
    )
  }

  if (error || !agent) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center text-red-700 dark:border-red-900/30 dark:bg-red-900/10 dark:text-red-400">
        <p className="font-medium">Error loading agent details. Please try again later.</p>
      </div>
    )
  }

  return (
    <article
      className="mx-auto flex w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-xl shadow-zinc-200/50 transition-all duration-300 dark:border-zinc-700 dark:bg-zinc-900 dark:shadow-none md:flex-row"
      aria-label={`Real Estate Agent ${agent.fullName}`}
    >
      {/* Visual Column */}
      <div className="relative flex flex-col items-center bg-zinc-50 p-8 text-center dark:bg-zinc-800/50 md:w-1/3 md:p-10">
        <div className="relative mb-6 h-32 w-32 overflow-hidden rounded-full border-4 border-white shadow-lg dark:border-zinc-700 md:h-40 md:w-40">
          {agent.avatar ? (
            <Image
              className="object-cover"
              src={agent.avatar}
              alt={agent.fullName}
              fill
              sizes="(max-width: 768px) 100vw, 33vw"
              priority
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-slate-100 text-3xl font-bold text-slate-600 dark:bg-slate-800 dark:text-slate-400">
              {agent.fullName
                .split(" ")
                .map((n: string) => n[0])
                .join("")
                .substring(0, 2)
                .toUpperCase()}
            </div>
          )}
        </div>

        <div className="flex items-center gap-1.5 flex-wrap justify-center">
          <HeadingText level={2} className="text-xl font-bold text-zinc-900 dark:text-zinc-50">
            {agent.fullName}
          </HeadingText>
          {/* Verified Badge */}
          <BadgeCheck className="h-5 w-5 text-blue-500 shrink-0" aria-label="Verified Agent" />
        </div>


        <Text className="mt-1 text-sm font-medium text-zinc-600 dark:text-zinc-400">
          {agent.profession || "Real Estate Partner"}
        </Text>

        {socialLinks.length > 0 && (
          <div className="mt-6">
            <SocialLinks
              links={socialLinks}
              spacing="small"
              buttonSize="icon"
              className="text-zinc-500 transition-colors hover:text-orange-500 dark:text-zinc-400 dark:hover:text-orange-400"
            />
          </div>
        )}
      </div>

      {/* Details Column */}
      <div className="flex flex-1 flex-col p-6 sm:p-8 md:p-10 bg-white dark:bg-zinc-900">
        <div className="mb-8">
          <HeadingText level={3} className="text-sm font-bold uppercase tracking-widest text-orange-600 dark:text-orange-500 mb-4">
            {translations.aboutAgent || "About the Agent"}
          </HeadingText>
          {agent.bio ? (
            <div className="prose-sm text-zinc-700 dark:text-zinc-300 leading-relaxed line-clamp-4">
              <ContentReader htmlContent={agent.bio} className="text-zinc-700 dark:text-zinc-300" />
            </div>
          ) : (
            <Text className="text-zinc-500 dark:text-zinc-400 italic">{translations.noBioAvailable || "No bio available."}</Text>
          )}
        </div>

        <div className="mt-auto space-y-4 rounded-xl bg-zinc-50 p-6 dark:bg-zinc-800/50">
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            {/* Contact Info Group */}
            <address className="flex flex-col gap-4 not-italic">
              {agent.phone && (
                <a
                  href={`tel:${agent.phone}`}
                  className="group flex items-center gap-3 text-sm text-zinc-700 transition-colors hover:text-orange-600 dark:text-zinc-300 dark:hover:text-orange-400"
                  aria-label={`Call ${agent.fullName}`}
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-zinc-500 shadow-sm ring-1 ring-zinc-200 transition-colors group-hover:text-orange-600 group-hover:ring-orange-200 dark:bg-zinc-700 dark:text-zinc-400 dark:ring-zinc-600 dark:group-hover:text-orange-400 dark:group-hover:ring-orange-500/30">
                    <Phone size={16} />
                  </div>
                  <span className="font-medium">{agent.phone}</span>
                </a>
              )}

              {agent.email && (
                <a
                  href={`mailto:${agent.email}`}
                  className="group flex items-center gap-3 text-sm text-zinc-700 transition-colors hover:text-orange-600 dark:text-zinc-300 dark:hover:text-orange-400"
                  aria-label={`Email ${agent.fullName}`}
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-zinc-500 shadow-sm ring-1 ring-zinc-200 transition-colors group-hover:text-orange-600 group-hover:ring-orange-200 dark:bg-zinc-700 dark:text-zinc-400 dark:ring-zinc-600 dark:group-hover:text-orange-400 dark:group-hover:ring-orange-500/30">
                    <Mail size={16} />
                  </div>
                  <span className="font-medium break-all">{agent.email}</span>
                </a>
              )}

              {agent.location && (agent.location.city || agent.location.country) && (
                <div className="flex items-center gap-3 text-sm text-zinc-700 dark:text-zinc-300">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-zinc-500 shadow-sm ring-1 ring-zinc-200 dark:bg-zinc-700 dark:text-zinc-400 dark:ring-zinc-600">
                    <MapPin size={16} />
                  </div>
                  <span className="font-medium">
                    {[agent.location.city, agent.location.state, agent.location.country].filter(Boolean).join(", ")}
                  </span>
                </div>
              )}
            </address>

            {/* Action Button */}
            <div className="mt-2 sm:mt-0 flex-shrink-0">
              <Button
                className="w-full sm:w-auto shadow-lg shadow-orange-500/20 hover:shadow-orange-500/40 bg-orange-500 hover:bg-orange-600 text-white font-semibold py-6 px-8 rounded-xl transition-all active:scale-95 whitespace-nowrap dark:bg-orange-600 dark:hover:bg-orange-700"
                onClick={() => (window.location.href = mailtoLink)}
              >
                {translations.contact || "Contact Agent"}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </article>
  )
}

const AgentDetailCard = ({
  detail,
  translations,
}: {
  detail: {
    id: string
    agentId: string
    title: string
    location: { address: string }
  }
  translations: Record<string, string>
}) => {
  return <ReactQueryProvider>
    <AgentDetail detail={detail} translations={translations} />
  </ReactQueryProvider>
}

export default React.memo(AgentDetailCard)